import {ref} from 'vue';
import {navigateTo, useLocalePrefix, useLogger, useRequestURL, useTranslation, useUniversalFetchRaw} from '#imports';
import {pushVJoySnackbar} from '@maltjoy/core-vue';

export type AdvancedSearchPromptOrigin = 'USER_PROMPT_HOMEPAGE_UNLOGGED' | 'USER_PROMPT_NAVBAR_UNLOGGED';

export interface AdvancedSearchSubmitOptions {
    briefOrigin?: string;
    pageSource?: string;
    origin?: AdvancedSearchPromptOrigin;
}

export function useAdvancedSearch() {
    const {t} = useTranslation();
    const logger = useLogger();
    const requestURL = useRequestURL();
    const {withLocalePrefix} = useLocalePrefix();

    const isSubmittingPrompt = ref<boolean>(false);

    async function submitAdvancedSearchPrompt(prompt: string, options: AdvancedSearchSubmitOptions = {}) {
        try {
            isSubmittingPrompt.value = true;

            const response = await useUniversalFetchRaw<void>('/search/api/advanced/prompt', {
                method: 'POST',
                body: {
                    prompt,
                    ...(options.origin && {origin: options.origin}),
                },
                baseURL: '',
            });

            const redirectLocation = response.headers.get('Location');
            if (redirectLocation) {
                const redirectUrl = new URL(redirectLocation, requestURL.origin);
                // For keyword search fallback (/s), no tracking params
                // For advanced search, add tracking params if provided
                const query =
                    redirectUrl.pathname === '/s'
                        ? undefined
                        : {
                              ...(options.briefOrigin && {brief_origin: options.briefOrigin}),
                              ...(options.pageSource && {pageSource: options.pageSource}),
                          };

                navigateTo(
                    {
                        path: withLocalePrefix(redirectLocation),
                        query,
                    },
                    {external: true},
                );
            }
        } catch (e: any) {
            if (e.statusCode === 400) {
                logger.warn('Advanced search failed due to harmful content', e);
                pushVJoySnackbar({props: {message: t('search.advanced.home-v2.generation.error.harmful.content'), level: 'error'}});
                return;
            }
            logger.error('Advanced search failed', e);
            pushVJoySnackbar({props: {message: t('generic.technical.error'), level: 'error'}});
        } finally {
            isSubmittingPrompt.value = false;
        }
    }

    return {
        isSubmittingPrompt,
        submitAdvancedSearchPrompt,
    };
}
